import React from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from '@mui/material'

import {
  UI_COLOR_BUTTON_MUTED,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_HOVER
} from '../../../../constants/uiColors'
import { REFUND_APPROVAL_TEXT } from '../../../constants/refundApprovalText'
import { NCR_NUMBER_MAX_LENGTH } from '../../../helpers/refundApprovalUtils'

const RefundNcrDialog = ({ open, ncrNumber, error, loading, onChange, onClose, onSave }) => {
  const canSave = ncrNumber.trim().length > 0 && !loading
  const remainingCharacters = Math.max(0, NCR_NUMBER_MAX_LENGTH - ncrNumber.length)

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 48px)', sm: '100%' },
          maxWidth: { xs: 420, sm: 600 },
          height: { xs: 'auto', sm: 300 },
          m: { xs: 3, sm: 4 }
        }
      }}
      aria-labelledby="refund-ncr-dialog-title"
    >
      <DialogTitle
        id="refund-ncr-dialog-title"
        sx={{
          fontSize: { xs: 22, sm: 24 },
          fontWeight: 700,
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 2.25, sm: 3 },
          pb: { xs: 1, sm: 2 }
        }}
      >
        {REFUND_APPROVAL_TEXT.ncrDialogTitle}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
        <DialogContentText sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: 14, sm: 16 } }}>
          {REFUND_APPROVAL_TEXT.ncrDialogDescription}
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          autoFocus
          fullWidth
          label={REFUND_APPROVAL_TEXT.ncrNumberLabel}
          placeholder={REFUND_APPROVAL_TEXT.ncrNumberPlaceholder}
          value={ncrNumber}
          disabled={loading}
          onChange={(event) => onChange(event.target.value.slice(0, NCR_NUMBER_MAX_LENGTH))}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && canSave) onSave()
          }}
          helperText={`${REFUND_APPROVAL_TEXT.maxCharacters} ${remainingCharacters}/${NCR_NUMBER_MAX_LENGTH}`}
          inputProps={{ maxLength: NCR_NUMBER_MAX_LENGTH }}
          size="small"
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          pb: { xs: 2.25, sm: 3 },
          pt: { xs: 0.75, sm: 1.5 },
          gap: 1
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={loading}
          sx={{
            color: UI_COLOR_BUTTON_MUTED,
            borderColor: UI_COLOR_BUTTON_MUTED,
            '&:hover': {
              borderColor: UI_COLOR_BUTTON_MUTED,
              bgcolor: 'transparent'
            }
          }}
        >
          {REFUND_APPROVAL_TEXT.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!canSave}
          disableElevation
          sx={{
            bgcolor: UI_COLOR_PRIMARY,
            '&:hover': {
              bgcolor: UI_COLOR_PRIMARY_HOVER
            }
          }}
        >
          {REFUND_APPROVAL_TEXT.ncrSave}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RefundNcrDialog
